import sortUtils from "@utils/sort.utils";
import i18n from "@plugins/i18n";
import store from "@plugins/store";
import DateTimeFormatter from "@utils/datetime.formatter";
import DateTimeUtils from "@utils/datetime.utils";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import { DEFAULT_END_TIME } from "@modules/assignment/service/assignment.constant";
import TreeUtils from "@plugins/tree-structure/tree.utils";
import { isEmpty, isNull } from "@utils/var.check";
import moment from "moment";

export default {
  sortClassesAndGroupsWithHeader(studentGroups) {
    const sortConfig = [{ field: "groupName", asc: true }];
    const sortedGroups = sortUtils.sortObjects(studentGroups, sortConfig);
    const classGroups = [],
      pinnedGroups = [];
    sortedGroups.forEach((group) => {
      if (!group.pinned) {
        classGroups.push(group);
      } else {
        pinnedGroups.push(group);
      }
    });

    const sortedGroupsWithHeader = [];
    if (pinnedGroups.length > 0) {
      sortedGroupsWithHeader.push({
        name: i18n.t("class_dropdown_header_pinned"),
        header: true,
      });
      sortedGroupsWithHeader.push(...pinnedGroups);
    }
    if (classGroups.length > 0) {
      sortedGroupsWithHeader.push({
        name: i18n.t("class_dropdown_header_class_groups"),
        header: true,
      });
      sortedGroupsWithHeader.push(...classGroups);
    }

    return sortedGroupsWithHeader;
  },
  sortClassesAndGroups(studentGroups) {
    const sortConfig = [{ field: "groupName", asc: true }];
    return sortUtils.sortObjects(studentGroups, sortConfig);
  },
  formattedStartDate(startDateTime) {
    return DateTimeFormatter.formatLocalDateTimeToDateOnly(startDateTime);
  },
  formattedStartTime(startDateTime) {
    return DateTimeFormatter.formatLocalDateTimeToTimeAmPm(startDateTime);
  },
  formattedEndDate(endDateTime) {
    return DateTimeFormatter.formatLocalDateTimeToDateOnly(endDateTime);
  },
  formattedEndTime(endDateTime) {
    return DateTimeFormatter.formatLocalDateTimeToTimeAmPm(endDateTime);
  },
  formattedDateTime(dateTime) {
    return DateTimeFormatter.formatLocalDateTimeToDateTime(dateTime);
  },
  isOverdue(endDateTime) {
    if (!endDateTime) {
      return false;
    }
    const dateTime = this.formattedDateTime(endDateTime);
    return DateTimeUtils.isNowAfterDate(dateTime);
  },
  getDefaultSelectedStudents(studentGroup) {
    return studentGroup.classListingStudentModels.map((student) => ({ id: student.studentInfoId }));
  },
  generateAssignmentNowTime() {
    const nowDateTime = new Date(store.state.app.serverTime);
    nowDateTime.setSeconds(0);
    nowDateTime.setMilliseconds(0);
    const nowTime = nowDateTime.toLocaleString("en-US", {
      timeZone: "Asia/Singapore",
    });
    return nowTime;
  },
  getDefaultSchedule(nowTime, lesson, academicYear) {
    const startDateTime = new Date(nowTime);
    const endDateTime = this.getDefaultEndDateTime(startDateTime, lesson, academicYear);
    return {
      assignNow: true,
      nowTime: nowTime,
      startDate: DateTimeFormatter.formatToDateOnly(startDateTime),
      endDate: DateTimeFormatter.formatToDateOnly(endDateTime),
      startTime: DateTimeFormatter.formatToTimeAmPm(startDateTime),
      endTime: DEFAULT_END_TIME,
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      remindEnableSharing: true,
    };
  },
  getCutoffDateTime(academicYear) {
    return moment()
      .set("year", academicYear)
      .set("month", 11)
      .set("date", 15)
      .set("hour", 23)
      .set("minute", 59)
      .set("second", 0)
      .format(DateTimeFormatter.formats.dateTime);
  },
  getDefaultEndDateTime(startDateTime, lesson, academicYear) {
    const cutoffDate = this.getCutoffDateTime(academicYear);
    let endDateTime = new Date(startDateTime);
    let duration = 7;
    const numSections = lesson.pages.filter(
      (page) => page.type === LESSON_ACTIVITY_TYPE.SECTION,
    ).length;
    if (numSections) {
      duration = numSections * 7;
    }

    endDateTime.setDate(endDateTime.getDate() + duration);
    endDateTime.setHours(23);
    endDateTime.setMinutes(59);
    endDateTime.setSeconds(0);
    if (!moment(endDateTime).isSameOrBefore(cutoffDate)) {
      endDateTime = cutoffDate;
    }
    return endDateTime;
  },
  getDefaultActivityAccessSettings(pages, assignmentStart, assignmentEnd) {
    return pages.reduce((settings, page) => {
      if (this.isActivityOrQuizActivity(page.type)) {
        settings[page.id] = {
          pageNo: page.pageNo,
          title: page.title,
          accessible: true,
          groupWork: false,
          restriction: null,
          restrictionSettings: [],
        };
      } else {
        settings[page.id] = {
          pageNo: page.pageNo,
          startDateTime: assignmentStart,
          endDateTime: assignmentEnd,
          assignNow: true,
        };
      }
      return settings;
    }, {});
  },
  populateAssignedLessonData(lesson) {
    const pages = lesson.pages.map((page) => ({
      id: page.id,
      type: page.type,
      pageNo: page.pageNo,
      parentId: page.parentId,
      title: page.title,
      optional: page.optional,
      appFeatureIds: page.appFeatureIds,
    }));
    const sortedPages = pages.sort((a, b) => a.pageNo - b.pageNo);
    const structuredPages = TreeUtils.constructTree(sortedPages);
    return {
      id: lesson.id,
      uuid: lesson.uuid,
      pages: structuredPages,
      title: lesson.title,
      hideSectionActivityNumber: lesson.hideSectionActivityNumber,
      showCover: lesson.showCover,
      showSectionCover: lesson.showSectionCover,
    };
  },
  isActivityOrQuizActivity(type) {
    return type === LESSON_ACTIVITY_TYPE.ACTIVITY || type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ;
  },
  // only for non-section completion status
  getAssignmentCompletionStatus(assignment) {
    if (isEmpty(assignment)) return "";

    const isOverdue = this.isOverdue(assignment.endDateTime);

    // If a category quantity is 0, it will not be considered as a category.
    let completed = {};
    if (assignment.totalCompleted > 0) {
      completed = {
        name: i18n.t("complete_label"),
        cssClass: "completed",
        quantity: assignment.totalCompleted,
      };
    }
    let incomplete = {};
    if (assignment.totalIncomplete > 0) {
      incomplete = {
        name: isOverdue ? i18n.t("overdue_label") : i18n.t("in_progress_label"),
        cssClass: isOverdue ? "overdue" : "in-progress",
        quantity: assignment.totalIncomplete,
      };
    }

    const completionStatusCategories = [];
    if (!isEmpty(completed)) {
      completionStatusCategories.push(completed);
    }
    if (!isEmpty(incomplete)) {
      completionStatusCategories.push(incomplete);
    }

    return completionStatusCategories;
  },
  // only for section completion status
  getSectionCompletionStatus(totalStudents, sectionDisplayModel) {
    if (isEmpty(totalStudents) || isEmpty(sectionDisplayModel)) return "";
    if (isNull(sectionDisplayModel.title)) return "";

    const isOverdue = this.isOverdue(sectionDisplayModel.endDateTime);

    let completed = {},
      incomplete = {};

    completed = {
      name: i18n.t("complete_label"),
      cssClass: "completed",
      quantity: sectionDisplayModel.numCompleted,
    };

    const numIncompleted = totalStudents - sectionDisplayModel.numCompleted;
    incomplete = {
      name: isOverdue ? i18n.t("overdue_label") : i18n.t("incomplete_label"),
      cssClass: isOverdue ? "overdue" : "incomplete",
      quantity: numIncompleted,
    };

    const completionStatusCategories = [];
    if (!isEmpty(completed)) {
      completionStatusCategories.push(completed);
    }
    if (!isEmpty(incomplete)) {
      completionStatusCategories.push(incomplete);
    }

    return completionStatusCategories;
  },
  // validation is activity based
  validateUnassignedTeamStudent(assignees, studentToAdd, subgroupName) {
    // construct students to be excluded and to be added to team
    const [teamStudents, studentExcluded] = studentToAdd.reduce(
      ([team, excluded], student) => {
        return assignees.findIndex((assigneeId) => assigneeId === student.studentInfoId) !== -1
          ? [[...team, student], excluded]
          : [team, [...excluded, student]];
      },
      [[], []],
    );

    // team data
    const data = {
      name: subgroupName,
      students: teamStudents.map((student) => {
        return {
          studentInfoId: student.studentInfoId,
        };
      }),
    };
    return {
      studentExcluded: studentExcluded.map((student) => {
        return {
          name: student.name,
          studentInfoId: student.studentInfoId,
        };
      }),
      data: data,
    };
  },
  // validation is activity based
  validateExistingTeamStudent(studentsInTeam, studentToAdd, subgroupName) {
    // construct students to be excluded and to be added to team
    const [teamStudents, studentExcluded] = studentToAdd.reduce(
      ([team, excluded], student) => {
        return studentsInTeam.findIndex(
          (studentInTeam) => studentInTeam.studentInfoId === student.studentInfoId,
        ) === -1
          ? [[...team, student], excluded]
          : [team, [...excluded, student]];
      },
      [[], []],
    );

    // team data
    const data = {
      name: subgroupName,
      students: teamStudents.map((student) => {
        return {
          studentInfoId: student.studentInfoId,
        };
      }),
    };
    return {
      studentExcluded: studentExcluded.map((student) => {
        return {
          name: student.name,
          studentInfoId: student.studentInfoId,
        };
      }),
      data: data,
    };
  },
  getValidStudents(allStudents, excludedStudents) {
    return allStudents.filter(
      (s) => excludedStudents.findIndex((e) => s.studentInfoId === e.studentInfoId) === -1,
    );
  },
};
